import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Mail, Lock, Phone, Shield, CheckCircle } from "lucide-react";
import { WizardFormData } from "@/types/wizard";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface Step7VerificationProps {
  formData: WizardFormData;
  updateFormData: (updates: Partial<WizardFormData>) => void;
  isDark: boolean;
}

const Step7Verification = ({ formData, updateFormData, isDark }: Step7VerificationProps) => {
  const [isCodeSent, setIsCodeSent] = useState(false);
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [sentVerificationCode, setSentVerificationCode] = useState("");

  const inputStyles = isDark
    ? "bg-[#0a0a0a] border-0 text-white shadow-[inset_4px_4px_8px_#000000,inset_-4px_-4px_8px_#141414] rounded-xl"
    : "bg-white border-0 text-gray-900 shadow-[inset_4px_4px_8px_#d1d1d1,inset_-4px_-4px_8px_#ffffff] rounded-xl";

  const labelStyles = isDark ? "text-white" : "text-gray-900";

  const cardStyles = {
    backgroundColor: isDark ? "#0a0a0a" : "#ffffff",
    boxShadow: isDark
      ? "4px 4px 8px #000000, -4px -4px 8px #141414"
      : "4px 4px 8px #d1d1d1, -4px -4px 8px #ffffff",
  };

  const sendVerificationCode = async () => {
    if (!formData.smsConsent) {
      toast.error("Please agree to receive SMS messages");
      return;
    }

    setIsSendingCode(true);
    try {
      const { data, error } = await supabase.functions.invoke('send-verification-sms', {
        body: {
          phoneNumber: formData.phone,
        },
      });

      if (error) throw error;

      if (data.success) {
        setSentVerificationCode(data.verificationCode);
        setIsCodeSent(true);
        toast.success(`Verification code sent to ${formData.phone}`);
      } else {
        throw new Error(data.error || 'Failed to send verification code');
      }
    } catch (error) {
      console.error('Error sending verification code:', error);
      toast.error("Failed to send verification code. Please try again.");
    } finally {
      setIsSendingCode(false);
    }
  };

  const verifyCode = () => {
    if (formData.verificationCode === sentVerificationCode) {
      updateFormData({ isVerified: true });
      toast.success("Phone verified successfully!");
    } else {
      toast.error("Invalid verification code. Please try again.");
    }
  };

  const otpSlotStyles = isDark
    ? "bg-[#0a0a0a] border-0 text-white shadow-[4px_4px_8px_#000000,-4px_-4px_8px_#141414] text-2xl"
    : "bg-white border-0 text-gray-900 shadow-[4px_4px_8px_#d1d1d1,-4px_-4px_8px_#ffffff] text-2xl";

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 
          className="text-2xl md:text-3xl font-bold mb-2"
          style={{ color: "#E4683B" }}
        >
          {formData.isVerified ? "Create Your Account" : "Verify Your Phone"}
        </h2>
        <p 
          className="text-lg opacity-80"
          style={{ color: isDark ? "#ffffff" : "#1a1a1a" }}
        >
          {formData.isVerified 
            ? "Set up your account to view your personalized results"
            : "We'll send you a verification code to confirm your number"
          }
        </p>
      </div>

      <div className="max-w-md mx-auto space-y-6">
        {!isCodeSent && !formData.isVerified && (
          <>
            {/* SMS Consent */}
            <div 
              className="p-4 rounded-xl"
              style={cardStyles}
            >
              <div className="flex items-start gap-3">
                <Checkbox
                  id="smsConsent"
                  checked={formData.smsConsent}
                  onCheckedChange={(checked) => updateFormData({ smsConsent: checked as boolean })}
                  className="mt-1"
                />
                <div>
                  <Label htmlFor="smsConsent" className={`cursor-pointer ${labelStyles}`}>
                    I agree to receive SMS messages from UDA ADU
                  </Label>
                  <p 
                    className="text-sm mt-1 opacity-70"
                    style={{ color: isDark ? "#ffffff" : "#1a1a1a" }}
                  >
                    By checking this box, you consent to receive text messages from UDA ADU 
                    regarding your ADU project. Message and data rates may apply. 
                    You can opt-out at any time by replying STOP.
                  </p>
                </div>
              </div>
            </div>

            <div className="text-center">
              <p 
                className="mb-4"
                style={{ color: isDark ? "#ffffff" : "#1a1a1a" }}
              >
                We'll send a verification code to: <strong>{formData.phone}</strong>
              </p>
              <Button
                onClick={sendVerificationCode}
                disabled={isSendingCode || !formData.smsConsent}
                className="px-8 py-3 rounded-xl font-semibold text-lg"
                style={{ 
                  backgroundColor: "#E4683B", 
                  color: "#ffffff",
                  opacity: !formData.smsConsent ? 0.5 : 1,
                }}
              >
                {isSendingCode ? (
                  <span className="flex items-center gap-2">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Sending...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Phone className="w-5 h-5" />
                    Send Verification Code
                  </span>
                )}
              </Button>
            </div>
          </>
        )}

        {isCodeSent && !formData.isVerified && (
          <div className="space-y-6">
            <div className="text-center">
              <Shield className="w-12 h-12 mx-auto mb-4" style={{ color: "#E4683B" }} />
              <p 
                className="mb-4"
                style={{ color: isDark ? "#ffffff" : "#1a1a1a" }}
              >
                Enter the 6-digit code sent to your phone
              </p>
            </div>

            <div className="flex justify-center">
              <InputOTP
                maxLength={6}
                value={formData.verificationCode}
                onChange={(value) => updateFormData({ verificationCode: value })}
              >
                <InputOTPGroup>
                  {[0, 1, 2, 3, 4, 5].map((index) => (
                    <InputOTPSlot key={index} index={index} className={otpSlotStyles} />
                  ))}
                </InputOTPGroup>
              </InputOTP>
            </div>

            <div className="text-center">
              <Button
                onClick={verifyCode}
                disabled={formData.verificationCode.length !== 6}
                className="px-8 py-3 rounded-xl font-semibold text-lg"
                style={{ backgroundColor: "#E4683B", color: "#ffffff" }}
              >
                Verify Code
              </Button>
              <button
                onClick={() => {
                  setIsCodeSent(false);
                  updateFormData({ verificationCode: "" });
                }}
                className="block mx-auto mt-3 text-sm underline"
                style={{ color: "#E4683B" }}
              >
                Didn't receive code? Try again
              </button>
            </div>
          </div>
        )}

        {formData.isVerified && (
          <div className="space-y-6">
            <div className="flex items-center justify-center gap-2 text-green-500 mb-6">
              <CheckCircle className="w-6 h-6" />
              <span className="font-medium">Phone Verified!</span>
            </div>

            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="email" className={labelStyles}>
                <Mail className="w-4 h-4 inline mr-2" style={{ color: "#E4683B" }} />
                Email Address *
              </Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => updateFormData({ email: e.target.value })}
                placeholder="you@example.com"
                className={inputStyles}
              />
            </div>

            {/* Password */}
            <div className="space-y-2">
              <Label htmlFor="password" className={labelStyles}>
                <Lock className="w-4 h-4 inline mr-2" style={{ color: "#E4683B" }} />
                Create Password *
              </Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => updateFormData({ password: e.target.value })}
                placeholder="Create a secure password"
                className={inputStyles}
              />
            </div>

            {/* Confirm Password */}
            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className={labelStyles}>
                <Lock className="w-4 h-4 inline mr-2" style={{ color: "#E4683B" }} />
                Confirm Password *
              </Label>
              <Input
                id="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={(e) => updateFormData({ confirmPassword: e.target.value })}
                placeholder="Confirm your password"
                className={inputStyles}
              />
              {formData.password && formData.confirmPassword && formData.password !== formData.confirmPassword && (
                <p className="text-red-500 text-sm">Passwords don't match</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Step7Verification;
